import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";

/**
 * 클라이언트 검색형 단일 셀렉트(콤보박스).
 * - options: [{ value, label }]
 * - value: 선택값, onChange(value)
 * 입력으로 라벨을 부분검색하고, 목록에서 선택한다. (브랜드 검색용)
 */
function Combobox({ options, value, onChange, placeholder, disabled }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  // 드롭다운이 닫혀 있을 때는 선택 라벨을 그대로 표시
  useEffect(() => {
    if (!open) setQuery(selected ? selected.label : "");
  }, [selected, open]);

  // 바깥 클릭 시 닫기
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
    return list.slice(0, 100);
  }, [open, query, options]);

  const choose = (opt) => {
    onChange(opt.value);
    setQuery(opt.label);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Wrap ref={wrapRef}>
      <Field
        value={open ? query : selected ? selected.label : ""}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => {
          setOpen(true);
          setQuery("");
          setActive(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      />
      {open && (filtered.length > 0 || query.trim()) && (
        <List>
          {filtered.length === 0 ? (
            <Empty>검색 결과가 없습니다</Empty>
          ) : (
            filtered.map((o, i) => (
              <ItemBtn
                key={o.value}
                type="button"
                active={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
              >
                {o.label}
              </ItemBtn>
            ))
          )}
        </List>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;
const Field = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 14px;
  font-size: 15px;
  border: 1px solid #d9dde2;
  border-radius: 10px;
  background: #ffffff;
  color: #222222;
  outline: none;
  &:focus {
    border-color: #003580;
  }
  &::placeholder {
    color: #b8bcc2;
  }
  &:disabled {
    background: #f6f7f9;
    color: #888888;
  }
`;
const List = styled.div`
  position: absolute;
  z-index: 30;
  top: 52px;
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  padding: 4px;
`;
const ItemBtn = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  font-size: 14px;
  color: #222;
  background: ${({ active }) => (active ? "#eef3fb" : "transparent")};
  border: none;
  border-radius: 8px;
  cursor: pointer;
`;
const Empty = styled.div`
  padding: 12px;
  font-size: 13px;
  color: #888;
  text-align: center;
`;

export default Combobox;
